<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'

const snackbarStore = useSnackbarStore()

// Define props - only custom props, VDialog props come from $attrs
interface Props {
  /** Padding size from all sides when fullscreen (CSS value or number in px) */
  fullscreenPadding?: string | number
  /** Border radius when fullscreen */
  fullscreenBorderRadius?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  fullscreenPadding: '12px',
  fullscreenBorderRadius: '12px'
})

const emit = defineEmits<{}>()

// Get all attributes passed to component
const attrs = useAttrs()

// Check if fullscreen prop is true from attributes
const isFullscreen = computed((): boolean => {
  return attrs.fullscreen === true || attrs.fullscreen === ''
})

// Classes for the dialog container
const dialogClasses = computed((): Record<string, boolean> => {
  return {
    'custom-fullscreen-dialog': isFullscreen.value
  }
})

// Content classes for internal VDialog styling
const contentClasses = computed((): string => {
  return isFullscreen.value ? 'custom-padded-content' : ''
})

// CSS variables for styles SCSS
const cssVariables = computed((): Record<string, string> => {
  const padding = typeof props.fullscreenPadding === 'number'
    ? `${props.fullscreenPadding}px`
    : props.fullscreenPadding

  const radius = typeof props.fullscreenBorderRadius === 'number'
    ? `${props.fullscreenBorderRadius}px`
    : props.fullscreenBorderRadius

  return {
    '--dialog-padding': padding,
    '--dialog-radius': radius
  }
})
</script>

<template>
  <VDialog
    v-bind="$attrs"
    :class="dialogClasses"
    :content-class="contentClasses"
    :style="cssVariables"
  >
    <template v-for="(_, slot) in $slots" #[slot]="scope">
      <slot :name="slot" v-bind="scope" />
    </template>
  </VDialog>
</template>

<style lang="scss">
.custom-fullscreen-dialog {
  // These styles will be applied to the VDialog component
  margin: var(--dialog-padding) !important;
  background-color: transparent !important;
  transition: all 0.3s ease !important;
}
.custom-padded-content {
  overflow: visible !important;
  border-radius: var(--dialog-radius) !important;
}
</style>
